import React, { useState, useEffect } from 'react';
import collegeConfig from '../../config/collegeConfig';
import AttachmentItem from './AttachmentItem';
import { getStatusBadge, formatDate, formatDateTime, renderReviewList } from './logbookDisplayUtils';
import { FaTimes, FaSpinner, FaPaperclip, FaCheckCircle } from 'react-icons/fa';

// Shared "Full View" details modal — used by ViewLogbooks.js (faculty/HOD, with a "Verify Now"
// button via onVerifyClick) and DeanDashboard.js (read-only, onVerifyClick omitted).
// Fetches the entry itself by id — GET /logbook/entries/:id is open to any non-student role
// and already populates courseId with `name code modules`, so this component is fully
// self-contained and needs no external `modules` prop.
const EntryDetailsModal = ({ entryId, onClose, onVerifyClick }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${collegeConfig.api.baseURL}/logbook/entries/${entryId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Failed to fetch logbook details');
        const data = await response.json();
        if (!cancelled) setEntry(data.entry);
      } catch (err) {
        console.error('Error fetching logbook details:', err);
        if (!cancelled) setError('Failed to load logbook details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetails();
    return () => { cancelled = true; };
  }, [entryId]);

  const getModuleName = (moduleId) =>
    entry?.courseId?.modules?.find(m => m.moduleId === moduleId)?.name || 'Unknown Module';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-sticky">
          <h3>Logbook Entry Details</h3>
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {loading ? (
          <div className="modal-loading">
            <FaSpinner className="spinner" />
            <p>Loading details...</p>
          </div>
        ) : entry ? (
          <div className="modal-body">
            <div className="detail-section">
              <h4>Student Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">
                    {entry.studentId?.profile?.firstName} {entry.studentId?.profile?.lastName}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{entry.studentId?.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Course:</span>
                  <span className="detail-value">
                    {entry.courseId?.name} ({entry.courseId?.code})
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Module:</span>
                  <span className="detail-value">{getModuleName(entry.moduleId)}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Submission Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Entry Type:</span>
                  <span className="detail-value">{entry.entryType}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Submitted On:</span>
                  <span className="detail-value">
                    {formatDateTime(entry.createdAt)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status:</span>
                  <span className={`status-badge ${getStatusBadge(entry.status).class}`}>
                    {getStatusBadge(entry.status).text}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Submitted Data</h4>

              {entry.entryType === 'single' && entry.entries ? (
                <div className="entries-container">
                  {entry.entries.map((e, index) => (
                    <div key={e.entryId || index} className="entry-card">
                      <div className="entry-header">
                        <h5>Entry #{index + 1}</h5>
                        <span className="entry-date">
                          {formatDate(e.submittedAt)}
                        </span>
                      </div>

                      <div className="entry-fields">
                        {(() => {
                          const module = entry.courseId?.modules?.find(m => m.moduleId === entry.moduleId);

                          if (module && module.fields) {
                            return module.fields.map(field => {
                              const value = e.data?.[field.key];
                              return (
                                <div key={field.key} className="field-item">
                                  <span className="field-label">{field.label}:</span>
                                  <span className="field-value">
                                    {value !== undefined && value !== null ?
                                      (field.type === 'date' ? formatDate(value) : value.toString())
                                      : 'N/A'}
                                  </span>
                                </div>
                              );
                            });
                          } else {
                            return e.data && typeof e.data === 'object' ?
                              Object.entries(e.data).map(([key, value]) => (
                                <div key={key} className="field-item">
                                  <span className="field-label">{key}:</span>
                                  <span className="field-value">
                                    {value !== undefined && value !== null ? value.toString() : 'N/A'}
                                  </span>
                                </div>
                              )) : (
                                <p className="no-data">No data available</p>
                              );
                          }
                        })()}
                      </div>

                      {e.attachments && e.attachments.length > 0 && (
                        <div className="entry-attachments">
                          <h6><FaPaperclip /> Attachments:</h6>
                          <div className="attachments-list">
                            {e.attachments.map((attachment, idx) => (
                              <AttachmentItem key={idx} attachment={attachment} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : entry.entryType === 'bulk' && entry.counters ? (
                <div className="counters-container">
                  <div className="counters-grid">
                    {(() => {
                      const module = entry.courseId?.modules?.find(m => m.moduleId === entry.moduleId);

                      if (module && module.fields) {
                        return module.fields.map(field => {
                          const value = entry.counters[field.key];
                          return (
                            <div key={field.key} className="counter-item">
                              <div className="counter-label">{field.label}</div>
                              <div className="counter-value">
                                {value !== undefined ? value : 0}
                                {field.target && (
                                  <span className="counter-target"> / {field.target}</span>
                                )}
                              </div>
                              {field.target && (
                                <div className="counter-progress">
                                  <div
                                    className="counter-progress-bar"
                                    style={{
                                      width: `${Math.min((value / field.target) * 100, 100)}%`,
                                      backgroundColor: value >= field.target ? '#2ECC71' : '#F39C12'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        });
                      } else {
                        return entry.counters && typeof entry.counters === 'object' ?
                          Object.entries(entry.counters).map(([key, value]) => (
                            <div key={key} className="counter-item">
                              <div className="counter-label">{key}</div>
                              <div className="counter-value">{value}</div>
                            </div>
                          )) : (
                            <p className="no-data">No counter data available</p>
                          );
                      }
                    })()}
                  </div>
                </div>
              ) : (
                <p className="no-data">No data available for this entry type</p>
              )}
            </div>

            {entry.attachments && entry.attachments.length > 0 && (
              <div className="detail-section">
                <h4><FaPaperclip /> Attachments</h4>
                <div className="attachments-list">
                  {entry.attachments.map((att, i) => (
                    <AttachmentItem key={i} attachment={att} />
                  ))}
                </div>
              </div>
            )}

            {entry.studentNotes && (
              <div className="detail-section">
                <h4>Student Notes</h4>
                <div className="notes-content">
                  {entry.studentNotes}
                </div>
              </div>
            )}

            {entry.reviewHistory?.length > 0 && renderReviewList(
              entry.reviewHistory,
              `Previous Attempt${entry.reviewHistory.length > 1 ? 's' : ''}`
            )}

            {entry.reattemptRequest && (
              <div className="detail-section verification-section">
                <h4>Reattempt Requested</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Requested By:</span>
                    <span className="detail-value">{entry.reattemptRequest.requestedByName}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Requested On:</span>
                    <span className="detail-value">{formatDateTime(entry.reattemptRequest.requestedAt)}</span>
                  </div>
                  {entry.reattemptRequest.note && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Note:</span>
                      <span className="detail-value">{entry.reattemptRequest.note}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {entry.review && entry.review.reviewerId && (
              <div className="detail-section verification-section">
                <h4>Verification Details</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Verified By:</span>
                    <span className="detail-value">{entry.review.reviewerName}</span>
                  </div>
                  {entry.review.reviewedAt && (
                    <div className="detail-item">
                      <span className="detail-label">Verified On:</span>
                      <span className="detail-value">
                        {formatDateTime(entry.review.reviewedAt)}
                      </span>
                    </div>
                  )}
                  {entry.review.rating && (
                    <div className="detail-item">
                      <span className="detail-label">Rating:</span>
                      <span className="detail-value">
                        {entry.review.rating === 'E' ? '⭐⭐⭐ Exceeds Expectations' :
                         entry.review.rating === 'M' ? '⭐⭐ Meets Expectations' :
                         '⭐ Below Expectations'}
                      </span>
                    </div>
                  )}
                  {entry.review.attemptActivity && (
                    <div className="detail-item">
                      <span className="detail-label">Attempt Activity:</span>
                      <span className="detail-value">
                        {entry.review.attemptActivity === 'F' ? 'First/Only' :
                         entry.review.attemptActivity === 'R' ? 'Repeat' : 'Remedial'}
                      </span>
                    </div>
                  )}
                  {entry.review.facultyDecision && (
                    <div className="detail-item">
                      <span className="detail-label">Faculty Decision:</span>
                      <span className="detail-value">
                        {entry.review.facultyDecision === 'C' ? 'Completed' :
                         entry.review.facultyDecision === 'R' ? 'Repeat' : 'Remedial'}
                      </span>
                    </div>
                  )}
                </div>
                {entry.review.feedback && (
                  <div className="review-feedback">
                    <h5>Remarks:</h5>
                    <p>{entry.review.feedback}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="modal-body">
            <p className="no-data">{error || 'Unable to load details'}</p>
          </div>
        )}

        <div className="modal-footer-sticky">
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
          {onVerifyClick && entry && !entry.review?.reviewerId && (
            <button
              className="btn-submit"
              onClick={() => {
                onClose();
                onVerifyClick(entry);
              }}
            >
              <FaCheckCircle /> Verify Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntryDetailsModal;
